import apiClient from '../lib/apiClient';
import {
  Category,
  CategoryMonth,
  CategoryWithMonthData,
  CreateCategoryData,
  UpdateCategoryData,
  CreateCategoryMonthData,
  UpdateCategoryMonthData,
  BulkPositionUpdateData,
} from '../types/category';

export const categoryService = {
  // Create category (group or sub-category)
  createCategory: async (data: CreateCategoryData): Promise<Category> => {
    return apiClient.post('/categories', data);
  },

  // Create category_month allocation
  createCategoryMonth: async (data: CreateCategoryMonthData): Promise<CategoryMonth> => {
    return apiClient.post('/categories/month-data', data);
  },

  // Get all categories for a plan
  getCategoriesByPlan: async (planId: string): Promise<Category[]> => {
    return apiClient.get(`/categories/plans/${planId}`);
  },

  // PERFORMANCE OPTIMIZED: Get categories AND budget status in a single API call
  getCategoriesWithBudgetStatus: async (planMonthId: string, excludeAccountId?: string): Promise<{
    categories: CategoryWithMonthData[];
    budget_status: {
      total_account_balance: number;
      total_remaining_category_balance: number;
      unallocated_amount: number;
      underfunded_amount: number;
      status: 'unallocated' | 'underfunded' | 'balanced';
    };
  }> => {
    const endpoint = excludeAccountId
      ? `/categories/plan-months/${planMonthId}/with-budget-status?exclude_account_id=${excludeAccountId}`
      : `/categories/plan-months/${planMonthId}/with-budget-status`;
    return apiClient.get(endpoint);
  },

  // Get categories with month data (hierarchical)
  getCategoriesByMonthPlan: async (planMonthId: string, excludeAccountId?: string): Promise<CategoryWithMonthData[]> => {
    const endpoint = excludeAccountId
      ? `/categories/plan-months/${planMonthId}?exclude_account_id=${excludeAccountId}`
      : `/categories/plan-months/${planMonthId}`;
    return apiClient.get(endpoint);
  },

  // Get specific category
  getCategoryById: async (categoryId: string): Promise<Category> => {
    return apiClient.get(`/categories/${categoryId}`);
  },

  // Update category
  updateCategory: async (categoryId: string, data: UpdateCategoryData): Promise<Category> => {
    return apiClient.put(`/categories/${categoryId}`, data);
  },

  // Update category_month allocation
  updateCategoryMonth: async (
    categoryMonthId: string,
    data: UpdateCategoryMonthData
  ): Promise<CategoryMonth> => {
    return apiClient.put(`/categories/month-data/${categoryMonthId}`, data);
  },

  // Update category positions (drag-and-drop reordering)
  updatePositions: async (data: BulkPositionUpdateData): Promise<{ message: string }> => {
    return apiClient.put('/categories/positions', data);
  },

  // Check if category has transactions before deletion
  checkCategoryTransactions: async (categoryId: string, currentMonthPlanId: string): Promise<{
    has_transactions: boolean;
    transaction_count: number;
  }> => {
    return apiClient.get(`/categories/${categoryId}/check-transactions?current_month_plan_id=${currentMonthPlanId}`);
  },

  // Delete category (from current + future months only)
  deleteCategory: async (categoryId: string, currentMonthPlanId: string): Promise<{
    message: string;
  }> => {
    return apiClient.delete(`/categories/${categoryId}?current_month_plan_id=${currentMonthPlanId}`);
  },

  // Apply allocation to future months
  applyAllocationToFutureMonths: async (
    categoryId: string,
    planId: string,
    currentMonthPlanId: string,
    sameAllocation: boolean,
    currentAllocated: number
  ): Promise<{ updated_count: number }> => {
    const params = new URLSearchParams({
      plan_id: planId,
      current_month_plan_id: currentMonthPlanId,
      same_allocation: String(sameAllocation),
      current_allocated: String(currentAllocated),
    });
    return apiClient.put(`/categories/${categoryId}/apply-to-future-months?${params}`);
  },

  // Get budget status (unallocated/underfunded calculation)
  getBudgetStatus: async (planMonthId: string): Promise<{
    total_account_balance: number;
    total_remaining_category_balance: number;
    unallocated_amount: number;
    underfunded_amount: number;
    status: 'unallocated' | 'underfunded' | 'balanced';
  }> => {
    return apiClient.get(`/categories/budget/status?plan_month_id=${planMonthId}`);
  },
};
